import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../auth/AuthProvider";
import { getSettings, updateSettings, fetchCategories } from "../lib/api";
import { ArrowLeft, Gear, FloppyDisk, BellRinging, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import ProtectedRoute from "../auth/ProtectedRoute";
import {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from "../lib/push";

function SettingsInner() {
  const [s, setS] = useState(null);
  const [threshold, setThreshold] = useState(0.15);
  const [minVol, setMinVol] = useState(100000);
  const [selectedCats, setSelectedCats] = useState([]);
  const [enableNotif, setEnableNotif] = useState(false);
  const [categories, setCategories] = useState([]);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getSettings(), fetchCategories()]).then(([d, c]) => {
      setS(d);
      setThreshold(d.edge_threshold ?? 0.15);
      setMinVol(d.min_volume ?? 100000);
      setSelectedCats(d.categories || []);
      setEnableNotif(!!d.enable_browser_notifications);
      setCategories(c.items || []);
    });
  }, []);

  const toggleCat = (slug) => {
    setSelectedCats((curr) =>
      curr.includes(slug) ? curr.filter((c) => c !== slug) : [...curr, slug]
    );
  };

  const handleEnableNotif = async (next) => {
    if (next && !isPushSupported()) {
      toast.error("Browser notifications not supported in this browser");
      return;
    }
    if (next) {
      if (Notification.permission !== "granted") {
        const p = await Notification.requestPermission();
        setPermission(p);
        if (p !== "granted") {
          toast.error("Notification permission denied");
          return;
        }
      }
      try {
        await subscribeToPush();
        toast.success("Browser notifications enabled · works even when tab is closed");
      } catch (e) {
        toast.error("Could not subscribe: " + (e?.message || e));
        return;
      }
    } else {
      try { await unsubscribeFromPush(); } catch {}
    }
    setEnableNotif(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateSettings({
        edge_threshold: Number(threshold),
        min_volume: Number(minVol),
        categories: selectedCats,
        enable_browser_notifications: enableNotif,
      });
      setS(updated);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <Header />
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Gear size={22} weight="duotone" className="text-edge" />
          <h1 className="font-display text-3xl font-medium">Alert Settings</h1>
        </div>

        {!s ? (
          <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest">
            Loading…
          </div>
        ) : (
          <div className="space-y-8">
            <div className="border border-white/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-edge mb-3">
                ◢ Edge threshold
              </div>
              <h2 className="font-display text-lg font-medium mb-1">
                Trigger alerts when AI edge ≥ <span className="text-edge font-mono">{(threshold * 100).toFixed(0)}%</span>
              </h2>
              <p className="text-sm text-zinc-400 font-body mb-5">
                Edge is the gap between MFSMAI Oracle's probability and Polymarket's YES price.
                Lower threshold = more alerts. Higher = only the strongest signals.
              </p>
              <input
                data-testid="settings-threshold-input"
                type="range"
                min="0.05"
                max="0.50"
                step="0.01"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="w-full accent-edge"
              />
              <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                <span>5%</span>
                <span>50%</span>
              </div>
            </div>

            <div className="border border-white/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-edge mb-3">
                ◢ Minimum market volume
              </div>
              <h2 className="font-display text-lg font-medium mb-1">
                Only alert on markets with ≥ <span className="text-edge font-mono">${Number(minVol).toLocaleString()}</span> total volume
              </h2>
              <p className="text-sm text-zinc-400 font-body mb-5">
                Filters out tiny illiquid markets where edge is mostly noise.
              </p>
              <input
                data-testid="settings-minvol-input"
                type="range"
                min="0"
                max="5000000"
                step="10000"
                value={minVol}
                onChange={(e) => setMinVol(Number(e.target.value))}
                className="w-full accent-edge"
              />
              <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                <span>$0</span>
                <span>$5M</span>
              </div>
            </div>

            <div className="border border-white/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-edge mb-3">
                ◢ Categories
              </div>
              <h2 className="font-display text-lg font-medium mb-1">
                Alert only on selected categories
              </h2>
              <p className="text-sm text-zinc-400 font-body mb-5">
                Leave all unselected to receive alerts across every category.
              </p>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => {
                  const active = selectedCats.includes(c.slug);
                  return (
                    <button
                      key={c.slug}
                      data-testid={`settings-cat-${c.slug}`}
                      onClick={() => toggleCat(c.slug)}
                      className={`text-xs font-body px-3 py-1.5 border transition-colors inline-flex items-center gap-1.5 ${
                        active
                          ? "border-edge bg-edge text-black"
                          : "border-white/15 text-zinc-300 hover:border-white/40"
                      }`}
                    >
                      {active && <CheckCircle size={12} weight="fill" />}
                      {c.label}
                    </button>
                  );
                })}
              </div>
              {selectedCats.length > 0 && (
                <button
                  data-testid="settings-clear-cats"
                  onClick={() => setSelectedCats([])}
                  className="mt-4 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1"
                >
                  <X size={11} /> Clear all
                </button>
              )}
            </div>

            <div className="border border-white/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-edge mb-3">
                ◢ Browser notifications
              </div>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="font-display text-lg font-medium mb-1 flex items-center gap-2">
                    <BellRinging size={18} weight="duotone" className="text-edge" />
                    Push desktop notifications for new alerts
                  </h2>
                  <p className="text-sm text-zinc-400 font-body">
                    Get a system notification the moment MFSMAI Oracle spots an edge that beats your
                    threshold. Works even when this tab is closed (via service-worker push).
                  </p>
                  {permission === "denied" && (
                    <p className="text-xs text-loss font-body mt-2">
                      Notification permission was denied in your browser settings.
                    </p>
                  )}
                  {permission === "unsupported" && (
                    <p className="text-xs text-zinc-500 font-body mt-2">
                      Your browser does not support notifications.
                    </p>
                  )}
                </div>
                <button
                  data-testid="settings-notif-toggle"
                  onClick={() => handleEnableNotif(!enableNotif)}
                  className={`shrink-0 relative w-12 h-6 transition-colors border ${
                    enableNotif ? "bg-edge border-edge" : "bg-zinc-900 border-white/15"
                  }`}
                  aria-pressed={enableNotif}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 bg-zinc-950 transition-all ${
                      enableNotif ? "left-7" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>

            <button
              data-testid="settings-save-btn"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-edge text-black font-display text-sm font-semibold uppercase tracking-[0.18em] px-5 py-3 hover:bg-white transition-colors disabled:opacity-60"
            >
              <FloppyDisk size={14} weight="fill" />
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  return (
    <ProtectedRoute>
      <SettingsInner />
    </ProtectedRoute>
  );
}
